import React from "react";
import {
  WhmMenuItem,
  WhmTypography,
  WhmDialog,
  WhmDialogTitle,
  WhmDialogContent,
  WhmDialogActions,
  WhmButton,
  WhmIconButton,
  WhmBox,
  useWhmSnackbar,
} from "@securitize/reactjs-whm";
import CloseIcon from "@mui/icons-material/Close";
import { Mailbox } from "../../../../shared/icons/mail-box";
import type { Theme } from "@securitize/reactjs-whm";
import {
  translationKeys,
  useTranslation,
} from "../../../../../hooks/useTranslation";
import { Translate } from "../../../../shared/translation";

const PostMailDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}> = ({ open, onClose, isMobile }) => {
  const snackbar = useWhmSnackbar();

  const { getTranslation: t } = useTranslation();

  const email = "investorsupport@securitize.io";
  const handleCopy = () => {
    navigator.clipboard
      .writeText(email)
      .then(() =>
        snackbar.show(t(translationKeys.DIALOG_POSTMAIL_SNACKBAR), {}),
      );
  };
  return (
    <>
      <WhmDialog
        open={open}
        onClose={onClose}
        aria-labelledby="postmail-dialog-title"
        aria-describedby="postmail-dialog-description"
        fullWidth
        {...(!isMobile && { maxWidth: "xs" })}
        fullScreen={isMobile}
      >
        <WhmBox
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <WhmDialogTitle
            id="postmail-dialog-title"
            variant="h4"
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Translate translationKey={translationKeys.DIALOG_POSTMAIL_TITLE} />
            <WhmIconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              sx={(theme: Theme) => ({
                padding: 0,
                paddingTop: "7px",
                alignSelf: "self-start",
                "&:hover": {
                  backgroundColor: "#fff",
                },
                color: theme.palette.common.black,
              })}
            >
              <CloseIcon />
            </WhmIconButton>
          </WhmDialogTitle>

          <WhmDialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            <WhmBox sx={{ display: "flex", justifyContent: "center" }}>
              <Mailbox />
            </WhmBox>
            <WhmTypography
              id="postmail-dialog-description"
              variant="body1"
              sx={{ paddingTop: "16px" }}
            >
              <Translate
                translationKey={translationKeys.DIALOG_POSTMAIL_BODY_1}
              />
            </WhmTypography>
            <WhmBox
              sx={{
                display: "flex",
                alignSelf: "center",
              }}
            >
              <WhmTypography
                variant="body1"
                sx={{
                  marginTop: "16px",
                  display: "flex",
                  alignItems: "center",
                  i: { color: "primary.main", cursor: "pointer" },
                  "&:hover": {
                    backgroundColor: "#fff",
                  },
                }}
              >
                <span style={{ textDecoration: "none" }}>
                  <Translate
                    translationKey={translationKeys.DIALOG_POSTMAIL_BODY_2}
                  />
                </span>
                <i
                  onClick={handleCopy}
                  className="wh-icon-copy"
                  style={{ marginLeft: "8px", color: "primary.main" }}
                />
              </WhmTypography>
            </WhmBox>
          </WhmDialogContent>

          <WhmDialogActions
            sx={{
              padding: isMobile ? "16px" : "8px",
              position: isMobile ? "sticky" : "static",
              bottom: isMobile ? 0 : "auto",
            }}
          >
            <WhmButton
              color="primary"
              onClick={onClose}
              variant="contained"
              fullWidth={isMobile}
              sx={{
                alignSelf: isMobile ? "stretch" : "flex-end",
              }}
            >
              <Translate
                translationKey={translationKeys.DIALOG_POSTMAIL_ACTION}
              />
            </WhmButton>
          </WhmDialogActions>
        </WhmBox>
      </WhmDialog>
    </>
  );
};

export const PostMailMenuItem = ({ isMobile }: { isMobile: boolean }) => {
  const [postMailModalOpen, setPostMailModalOpen] = React.useState(false);

  const handlePostMailClick = () => {
    setPostMailModalOpen(true);
  };

  const handleModalClose = () => {
    setPostMailModalOpen(false);
  };

  return (
    <>
      <WhmMenuItem onClick={handlePostMailClick}>
        <i
          className="wh-icon wh-icon-postmail"
          style={{ paddingRight: "8px", fontSize: "20px", color: "#6c6c6c" }}
        />
        <WhmTypography variant="body1">
          <Translate
            translationKey={
              translationKeys.DOCUMENT_DATAGRID_MENU_POSTMAIL_LABEL
            }
          />
        </WhmTypography>
      </WhmMenuItem>
      <PostMailDialog
        open={postMailModalOpen}
        onClose={handleModalClose}
        isMobile={isMobile}
      />
    </>
  );
};
